import {
  WSActions,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useWSMutation } from "@ws-react-query";
import { format } from "date-fns";
import { useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { QUERY_INTERNAL_ACCOUNTS } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { QUERY_VERIFICATION_MISSING_DATA } from "../../../../query/onboarding/queries/useQueryVerificationMissingFields";
import { QUERY_VERIFICATIONS } from "../../../../query/onboarding/queries/useQueryVerifications";
import { updateCustomerData } from "../../../../services/api/onboarding/customer";
import { DataView } from "../../../../shared/components/DataView";
import { getLast4 } from "../../../../shared/utils/getLast4";
import { getTaxIdLabel } from "../../../../shared/utils/taxId";
import { ConfirmationDisclosures } from "../ConfirmationDisclosures";
import { phoneFormDataToE164 } from "../FormPartialPhoneNumber";
import { ReviewPerson } from "../ReviewPerson";
import { FormDataAdditionalInfoRepresentative } from "./FormAdditionalInfoRepresentative";

type Props = {
  formData?: FormDataAdditionalInfoRepresentative;
  onBack?: () => void;
  onNext?: () => void;
  country?: string;
};

export const Review: React.FC<Props> = props => {
  const [consent, setConsent] = useState(false);

  const [submit, meta] = useWSMutation(
    async () => {
      if (!props.formData?.representative) {
        throw new Error("No representative set");
      }

      // 1. Update representative data
      await updateCustomerData("Representative", {
        customerData: {
          individualTaxId: props.formData.representative.taxId || undefined
        },
        ownershipStake: props.formData.representative.ownershipStake ?? 0
      });

      // 2. Update beneficial owners data if provided
      const beneficialOwners = props.formData?.beneficialOwners || [];

      if (beneficialOwners.length > 0) {
        for (let i = 0; i < beneficialOwners.length; i++) {
          const beneficialOwner = beneficialOwners[i];
          await updateCustomerData(`Owner${i + 1}` as any, {
            customerData: {
              firstName: beneficialOwner.firstName || undefined,
              middleName: beneficialOwner.middleName || undefined,
              lastName: beneficialOwner.lastName || undefined,
              occupation: beneficialOwner.jobTitle || undefined,
              dateOfBirth: beneficialOwner.birthday
                ? format(beneficialOwner.birthday, "yyyy-MM-dd")
                : undefined,
              email: beneficialOwner.email || undefined,
              phoneNumber: beneficialOwner.phone
                ? phoneFormDataToE164(beneficialOwner.phone)
                : undefined,
              country: beneficialOwner.country || undefined,
              individualTaxId: beneficialOwner.taxId || undefined,
              addressLine1: beneficialOwner.address.addressLine1 || undefined,
              addressLine2: beneficialOwner.address.addressLine2 || undefined,
              city: beneficialOwner.address.city || undefined,
              region: beneficialOwner.address.state || undefined,
              postalCode: beneficialOwner.address.postalCode || undefined
            },
            ownershipStake: beneficialOwner.ownershipStake ?? 0
          });
        }
      }
    },
    {
      onSuccess: props.onNext,
      dependencies: [
        QUERY_VERIFICATIONS,
        QUERY_VERIFICATION_MISSING_DATA,
        QUERY_INTERNAL_ACCOUNTS
      ]
    }
  );

  return (
    <WSList gap="2XL">
      <WSCard header="Review & submit">
        <WSText.ParagraphSm color="gray600">
          Please review the information you provided for accuracy and submit to
          continue.
        </WSText.ParagraphSm>
      </WSCard>

      <DataView
        title="Representative information (You)"
        onEdit={props.onBack}
        data={[
          {
            label: getTaxIdLabel("Individual", props.country || "US"),
            value:
              !!props.formData?.representative?.taxId &&
              typeof props.formData?.representative?.taxId === "string"
                ? `••• ${getLast4(props.formData.representative.taxId)}`
                : undefined,
            fsExclude: true
          },
          {
            label: "Ownership stake",
            value: props.formData?.representative?.ownershipStake
              ? `${props.formData?.representative?.ownershipStake}%`
              : undefined
          }
        ]}
      />

      {props.formData?.beneficialOwners?.map((owner, index) => (
        <ReviewPerson
          key={index}
          type="BeneficialOwner"
          formData={owner}
          onEdit={props.onBack}
        />
      ))}

      <WSCard>
        <ConfirmationDisclosures
          consent={consent}
          setConsent={setConsent}
          verificationLevel="Banking"
        />
      </WSCard>

      <WSErrorMessage contextKey="MonitoredEntity" error={meta.error} />

      <WSActions
        alignment="fill"
        buttons={[
          { label: "Back", onClick: props.onBack, kind: "Secondary" },
          {
            label: "Confirm & continue",
            disabled: !consent,
            loading: meta.isLoading,
            onClick: submit
          }
        ]}
      />
    </WSList>
  );
};
